/** Display title for general care plans (PHA-674). Internal disease values stay unchanged. */
export const GENERAL_CARE_PLAN_TITLE = 'General Care Plan';

/**
 * Friendly display names for patient-facing PDF titles (PHA-674).
 * Keys are canonical lowercase labels produced by the care-plan generation prompt
 * (generate_care_plan.txt §2.2.x). This map is COUPLED to that prompt template —
 * whenever the prompt's canonical disease labels change, update the keys here in lockstep.
 * Falls back to the official/canonical name (capitalizeFirst) when not in the map.
 */
const FRIENDLY_DISEASE_NAMES: Record<string, string> = {
  'hypertension': 'High Blood Pressure',
  'type 2 diabetes mellitus': 'Diabetes',
  'hyperlipidemia': 'High Cholesterol',
  'chronic kidney disease': 'Kidney Disease',
  'heart failure': 'Heart Failure',
  'atrial fibrillation': 'Atrial Fibrillation',
  'copd': 'COPD',
};

/** Capitalize the first character of a string; leave the rest unchanged. */
function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Known medical abbreviations that should render fully uppercased in official titles. */
const ABBREVIATIONS = new Set(['copd', 'chf', 'ckd', 'htn', 'cad', 'esrd', 'pad', 'gerd', 'afib', 'dm', 'mi', 'tia', 'dvt', 'pe', 'ldl', 'hdl']);

/**
 * Title-cases a disease name for official (care-team) surfaces.
 * Each word is capitalised; known abbreviations are uppercased in full.
 * E.g. "atrial fibrillation" → "Atrial Fibrillation", "copd" → "COPD".
 */
function toOfficialTitleCase(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(w => ABBREVIATIONS.has(w.toLowerCase()) ? w.toUpperCase() : capitalizeFirst(w.toLowerCase()))
    .join(' ');
}

/** Legacy hospitalization-only definition and generated generic plan disease field. */
export function isGeneralCarePlanDisease(disease: string | null | undefined): boolean {
  if (!disease) return false;
  const normalized = disease.trim().toLowerCase();
  return normalized === 'general' || normalized === 'general care plan';
}

/**
 * The single merged care plan emitted by generation (prompt §3.1 always sets
 * `disease: "global"`). This is the umbrella covering the patient's real conditions —
 * NOT a "general"/generic plan — so it is kept separate from isGeneralCarePlanDisease()
 * (which drives selection/filtering, see findGeneralCarePlanDefinition). This predicate is
 * display-only: it stops the internal "global" token from reaching patient-facing copy.
 */
export function isMergedGlobalDisease(disease: string | null | undefined): boolean {
  if (!disease) return false;
  return disease.trim().toLowerCase() === 'global';
}

/**
 * Produces a comma-joined label from a list of disease names (PHA-674).
 * When friendly=true, maps each name through FRIENDLY_DISEASE_NAMES keyed on its
 * lowercase form, falling back to capitalizeFirst(name) for unmapped entries.
 * De-dupes case-insensitively. Returns null when the list is effectively empty.
 */
export function formatDiseaseList(diseases: string[], friendly: boolean = false): string | null {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const d of diseases) {
    const trimmed = d.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    if (friendly) {
      names.push(FRIENDLY_DISEASE_NAMES[key] ?? capitalizeFirst(trimmed));
    } else {
      names.push(toOfficialTitleCase(trimmed));
    }
  }
  if (names.length === 0) return null;
  return names.join(', ');
}

/**
 * Title-cases a single managed-condition name for patient-facing display (PHA-767).
 * Uses the SAME official casing as the PDF title (toOfficialTitleCase) so the
 * "Chronic Conditions Managed" bullets can never drift from the "Patient Care Plan
 * for …" heading. When friendly=true, maps through FRIENDLY_DISEASE_NAMES first.
 */
export function formatDiseaseName(name: string, friendly: boolean = false): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return trimmed;
  if (friendly) {
    return FRIENDLY_DISEASE_NAMES[trimmed.toLowerCase()] ?? capitalizeFirst(trimmed);
  }
  return toOfficialTitleCase(trimmed);
}

/**
 * PHA-773: stable fallback source for the covered-disease list. The `diseases[]`
 * rollup (PHA-674) can arrive empty when a generation run leaves per-item disease
 * tags sparse, or on read paths that don't compute the rollup. Each ICD-10 code
 * carries its canonical template disease in `clinical_disease` (PHA-755) — exactly
 * one per code — so the de-duped set is a clean, deterministic substitute that keeps
 * the title off the internal "global" token and off "General Care Plan".
 */
export function diseasesFromIcd10Codes(
  icd10Codes?: { clinical_disease?: string | null }[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of icd10Codes ?? []) {
    const d = (c?.clinical_disease ?? '').trim();
    if (!d) continue;
    const key = d.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(d);
  }
  return out;
}

/**
 * True when a merged "global" plan is really the generic catch-all ("General care plan").
 *
 * The generic generation path (generate_care_plan.txt §3.3.2) emits `disease: "global"`
 * with per-item `diseases` populated for each inferred condition being monitored (e.g.
 * asthma, hypothyroidism) — so the covered-disease rollup is NOT empty and would otherwise
 * title the plan "Care Plan for <inferred conditions>". The distinguishing signal is that
 * every ICD-10 code is tagged `clinical_disease = "General care plan"` (the
 * `_infer_icd10_fallback_if_empty` default), since the generic prompt forbids per-disease
 * ICD-10 attribution. When that holds, the plan is general and must be titled accordingly.
 */
export function isGeneralGlobalPlan(
  icd10Codes?: { clinical_disease?: string | null }[]
): boolean {
  const tagged = (icd10Codes ?? [])
    .map((c) => (c?.clinical_disease ?? '').trim())
    .filter(Boolean);
  if (tagged.length === 0) return false;
  return tagged.every((d) => isGeneralCarePlanDisease(d));
}

/** Pick the disease rollup, falling back to ICD-10 clinical_disease tags when empty (PHA-773). */
function resolveCoveredDiseases(
  diseases?: string[],
  icd10Codes?: { clinical_disease?: string | null }[]
): string[] {
  return diseases && diseases.length > 0 ? diseases : diseasesFromIcd10Codes(icd10Codes);
}

/**
 * True when the plan must be titled as the generic catch-all. Prefers the authoritative
 * backend `is_general` flag (set by `backend.is_general_care_plan`); falls back to the
 * ICD-10 clinical_disease heuristic on surfaces that don't have the flag (e.g. unpersisted
 * approval drafts).
 */
function shouldTitleAsGeneral(
  isGeneral: boolean | undefined,
  icd10Codes?: { clinical_disease?: string | null }[]
): boolean {
  if (isGeneral === true) return true;
  // PHA-818: an explicit backend flag is authoritative — don't let the clinical_disease
  // heuristic override it. The backend now recovers covered diseases from ICD-10 (registry)
  // when the rollup is empty, so is_general=false means a genuine multi-condition plan even
  // if every ICD-10 row is tagged the legacy/backfilled "General care plan". Only fall back
  // to the heuristic when the flag is absent (e.g. unpersisted approval drafts).
  if (isGeneral === false) return false;
  return isGeneralGlobalPlan(icd10Codes);
}

/** Patient Care Plans tab and similar "Care Plan for …" headings. */
export function formatPatientCarePlanHeading(
  disease: string,
  diseases?: string[],
  icd10Codes?: { clinical_disease?: string | null }[],
  isGeneral?: boolean
): string {
  if (isGeneralCarePlanDisease(disease)) return GENERAL_CARE_PLAN_TITLE;
  if (isMergedGlobalDisease(disease)) {
    if (shouldTitleAsGeneral(isGeneral, icd10Codes)) return GENERAL_CARE_PLAN_TITLE;
    const list = formatDiseaseList(resolveCoveredDiseases(diseases, icd10Codes), false);
    if (!list) return GENERAL_CARE_PLAN_TITLE;
    return `Care Plan for ${list}`;
  }
  return `Care Plan for ${disease}`;
}

/** Card headers during approval/review (no "Care Plan for" prefix). */
export function formatCarePlanCardTitle(
  disease: string,
  diseases?: string[],
  icd10Codes?: { clinical_disease?: string | null }[],
  isGeneral?: boolean
): string {
  if (isGeneralCarePlanDisease(disease)) return GENERAL_CARE_PLAN_TITLE;
  if (isMergedGlobalDisease(disease)) {
    if (shouldTitleAsGeneral(isGeneral, icd10Codes)) return GENERAL_CARE_PLAN_TITLE;
    const list = formatDiseaseList(resolveCoveredDiseases(diseases, icd10Codes), false);
    if (!list) return GENERAL_CARE_PLAN_TITLE;
    return list;
  }
  return disease;
}

/**
 * PDF section headers (e.g. "Hypertension Care Plan").
 * When friendly=true, maps disease names through FRIENDLY_DISEASE_NAMES for patient-facing output.
 */
export function formatPdfCarePlanSectionTitle(
  disease: string,
  diseases?: string[],
  friendly?: boolean,
  icd10Codes?: { clinical_disease?: string | null }[],
  isGeneral?: boolean
): string {
  if (isGeneralCarePlanDisease(disease)) return GENERAL_CARE_PLAN_TITLE;
  if (isMergedGlobalDisease(disease)) {
    if (shouldTitleAsGeneral(isGeneral, icd10Codes)) return GENERAL_CARE_PLAN_TITLE;
    const list = formatDiseaseList(resolveCoveredDiseases(diseases, icd10Codes), friendly ?? false);
    if (!list) return GENERAL_CARE_PLAN_TITLE;
    return `${list} Care Plan`;
  }
  // Single known disease
  const name =
    friendly
      ? (FRIENDLY_DISEASE_NAMES[disease.trim().toLowerCase()] ?? capitalizeFirst(disease.trim()))
      : disease;
  return `${name} Care Plan`;
}

/**
 * PHA-842: Patient-level document title for the care-plan PDF. The PDF aggregates ALL of a
 * patient's care plan definitions into one heading, so this applies the same general-plan
 * gate as formatPatientCarePlanHeading (is_general flag → ICD-10 heuristic) but across every
 * definition. It is DECOUPLED from the "Chronic Conditions Managed" list
 * (deriveManagedConditionNames): a no-diagnosis plan is titled "General Care Plan" — exactly
 * as the in-app heading reads — while §3 still enumerates the real (even uncurated) conditions.
 *
 * Resolution:
 *   - real conditions present  → "Patient Care Plan for <conditions>"
 *   - only general plan(s)     → GENERAL_CARE_PLAN_TITLE ("General Care Plan")
 *   - no definitions at all    → "Patient Care Plan" (neutral)
 */
export function formatPdfDocumentTitle(
  carePlanDefinitions?: {
    disease: string;
    diseases?: string[];
    icd10_codes?: { clinical_disease?: string | null }[];
    is_general?: boolean;
  }[]
): string {
  const defs = carePlanDefinitions ?? [];
  if (defs.length === 0) return 'Patient Care Plan';
  const realConditions: string[] = [];
  let sawGeneralPlan = false;
  for (const cpd of defs) {
    if (isGeneralCarePlanDisease(cpd.disease)) {
      sawGeneralPlan = true;
      continue;
    }
    if (isMergedGlobalDisease(cpd.disease)) {
      if (shouldTitleAsGeneral(cpd.is_general, cpd.icd10_codes)) {
        sawGeneralPlan = true;
        continue;
      }
      realConditions.push(...resolveCoveredDiseases(cpd.diseases, cpd.icd10_codes));
    } else {
      realConditions.push(cpd.disease);
    }
  }
  const list = formatDiseaseList(realConditions, false);
  if (list) return `Patient Care Plan for ${list}`;
  return sawGeneralPlan ? GENERAL_CARE_PLAN_TITLE : 'Patient Care Plan';
}

interface ManagedConditionSource {
  care_plan_definitions?: {
    disease: string;
    diseases?: string[];
    icd10_codes?: { description?: string; clinical_disease?: string | null }[];
  }[];
  problems?: { problem?: string; clinical_status?: string }[];
}

/**
 * PHA-739 / PHA-767: Real, patient-readable condition names for the "Chronic Conditions
 * Managed" section. The merged plan's `disease` is the internal "global" umbrella token,
 * so it must never be surfaced — instead list the conditions that plan actually covers.
 *
 * Source (rollup-only):
 *   1. Pre-computed diseases[] rollup passed as the `diseases` param.
 *   2. Per-CPD diseases[] rollup from care_plan_definition_diseases view (PHA-674) for
 *      merged/global/general plans.
 *   3. For normal single-disease plans, the `cpd.disease` value itself.
 *
 * ICD-10 codes and the patient problem list are NOT used — ICD-10 ranges 1→32+ entries
 * and the problem list produces noise. When the rollup is empty, returns [] so callers
 * can fall back to a generic "medical record" label.
 *
 * Returns de-duplicated names in source order; empty array if nothing usable is available.
 */
export function deriveManagedConditionNames(
  patient: ManagedConditionSource,
  diseases?: string[]
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const push = (raw?: string | null) => {
    const n = (raw ?? '').trim();
    if (!n) return;
    // PHA-842: never surface the internal umbrella ("global") or generic ("General care
    // plan") tokens as a managed condition. For a no-Dx plan the ICD-10 fallback yields
    // "General care plan"; dropping it lets §3 read the real covered conditions (or the
    // generic medical-record line) instead of echoing the plan-type label.
    if (isGeneralCarePlanDisease(n) || isMergedGlobalDisease(n)) return;
    const key = n.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    names.push(n);
  };

  // If a pre-computed diseases[] list is provided, use it directly.
  if (diseases && diseases.length > 0) {
    diseases.forEach((d) => push(d));
    return names;
  }

  (patient.care_plan_definitions || []).forEach((cpd) => {
    if (isMergedGlobalDisease(cpd.disease) || isGeneralCarePlanDisease(cpd.disease)) {
      // Prefer the per-CPD diseases[] rollup; when it is empty (PHA-773 sparse-tag
      // generations) fall back to the ICD-10 clinical_disease tags (PHA-755) — one
      // canonical disease per code — before contributing nothing.
      if (cpd.diseases && cpd.diseases.length > 0) {
        cpd.diseases.forEach((d) => push(d));
      } else {
        diseasesFromIcd10Codes(cpd.icd10_codes).forEach((d) => push(d));
      }
    } else {
      push(cpd.disease);
    }
  });

  return names;
}

export function findGeneralCarePlanDefinition<T extends { disease: string }>(
  definitions: T[] | undefined
): T | undefined {
  if (!definitions?.length) return undefined;
  return (
    definitions.find((cpd) => cpd.disease?.trim().toLowerCase() === 'general care plan') ??
    definitions.find((cpd) => isGeneralCarePlanDisease(cpd.disease))
  );
}

/**
 * PHA-739: Canonical headings for the patient-friendly care plan section.
 * Shared between CarePlanDisplay.tsx (online) and generateCarePlanPdf.ts (PDF)
 * so the two surfaces can never drift from each other.
 */
export const PATIENT_FRIENDLY_HEADINGS = {
  /** Top-level section title used in both the tab content and the PDF section. */
  SECTION: 'Your Care Plan in Plain Language',
  GOALS: 'Your Health Goals',
  MEDICATIONS: 'Your Medications & Why',
  TASKS: 'What To Do',
  BARRIERS: 'Challenges',
  WARNING_SIGNS: 'When To Call Your Doctor',
} as const;

// ---------------------------------------------------------------------------
// PHA-850: group Voice-of-the-Patient barriers under their relevant goal.
//
// Person-centered barriers belong in the context of the goal they obstruct, not
// in a free-floating "Challenges" list. Today barriers carry NO stored link to a
// goal (the `pcm_barriers` table has no `pco_ref`), even though the LLM generates
// them per-goal — that link is added by PHA-851 (additive goals). This helper is
// written to be forward-compatible with that change:
//   • If ANY barrier carries a `pco_ref`, barriers are grouped strictly by it.
//     Barriers whose `pco_ref` matches no goal fall through to `unassigned`.
//   • Otherwise (today's data, no link): a SINGLE goal owns every barrier (the
//     real, current case — see PHA-850 / patient m17tst100), while MULTIPLE goals
//     leave all barriers `unassigned` rather than guess an attribution.
// Both inputs are sorted by `sort_order` so render order is deterministic.
// ---------------------------------------------------------------------------

export interface PcoGoalForGrouping {
  pco_ref?: string;
  sort_order?: number;
}

export interface PcmBarrierForGrouping {
  barrier_ref?: string;
  /** Optional goal link — present only once PHA-851 lands; absent in today's data. */
  pco_ref?: string | null;
  sort_order?: number;
}

export interface BarriersGroupedByGoal<G, B> {
  /** Goals in sort order, each paired with the barriers that belong to it. */
  perGoal: Array<{ goal: G; barriers: B[] }>;
  /**
   * Barriers not attributable to any single goal. Rendered as a shared
   * "Barriers we're working on together" block. Empty in the common
   * single-goal case (everything nests under that goal).
   */
  unassigned: B[];
}

export function groupBarriersByGoal<
  G extends PcoGoalForGrouping,
  B extends PcmBarrierForGrouping,
>(goals: G[] | null | undefined, barriers: B[] | null | undefined): BarriersGroupedByGoal<G, B> {
  const sortedGoals = [...(goals ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const sortedBarriers = [...(barriers ?? [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const hasLink = sortedBarriers.some((b) => b.pco_ref != null && b.pco_ref !== '');

  if (hasLink) {
    const goalRefs = new Set(sortedGoals.map((g) => g.pco_ref).filter((r): r is string => r != null && r !== ''));
    const perGoal = sortedGoals.map((goal) => ({
      goal,
      barriers: sortedBarriers.filter((b) => b.pco_ref === goal.pco_ref),
    }));
    const unassigned = sortedBarriers.filter((b) => b.pco_ref == null || b.pco_ref === '' || !goalRefs.has(b.pco_ref));
    return { perGoal, unassigned };
  }

  // No stored links. One goal owns every barrier; multiple goals → shared block.
  if (sortedGoals.length === 1) {
    return { perGoal: [{ goal: sortedGoals[0], barriers: sortedBarriers }], unassigned: [] };
  }
  return { perGoal: sortedGoals.map((goal) => ({ goal, barriers: [] })), unassigned: sortedBarriers };
}
